import { NodeApi, NodeHelpersType } from '@candulabs/craft';
import React, { ReactElement } from 'react';

import { isASubmitFormStep } from '@candu-ai/forms/editor-utils';
import Node from 'craft/components/Node';
import Placeholder from 'craft/components/Placeholder';
import { BuiltInComponents } from 'craft/constants';
import { useCss, useEditor, useNodeContext } from 'craft/hooks';
import { CraftNode, Resolver, ToolboxComponentSections } from 'craft/types';

interface Props {
  placeholder?: React.ReactNode;
  children?: ReactElement | ReactElement[];
  stepId: string;
}

export const FormStep: Resolver = ({ children, placeholder, stepId }: Props) => {
  const { id: nodeId } = useNodeContext();
  const { query } = useEditor();
  const formStepNode = query.node(nodeId).get();

  const formNode = query.node(formStepNode.data.parent).get();
  const isASubmitStep = isASubmitFormStep(formNode, formStepNode);
  const isAnEmptyNormalStep = !isASubmitStep && formStepNode.data.nodes.length === 0;

  const placeholderComponent = placeholder || (
    <Placeholder
      placeholderText={
        !isAnEmptyNormalStep
          ? 'Drag any element into this area.'
          : 'Drag form fields or other elements into this area.'
      }
    />
  );

  const { css } = useCss([formNode.id]);

  return (
    <Node overrideCss={css} data-type="form-step" data-step-id={stepId}>
      {isAnEmptyNormalStep ? (
        <>
          {placeholderComponent}
          {children}
        </>
      ) : (
        children || placeholderComponent
      )}
    </Node>
  );
};

export const isSuccessFormStep = (node?: CraftNode): boolean =>
  node?.data?.props?.type === 'success';

FormStep.craft = {
  displayName: (node: CraftNode, helpers: (nodeId?: string) => NodeApi) => {
    if (isSuccessFormStep(node)) {
      return 'Success';
    }
    const parentChildren = helpers(node.data.parent).childNodes();
    if (parentChildren.length > 2) {
      return `Form Step ${parentChildren.indexOf(node.id) + 1}`;
    }
    return 'Form Step';
  },
  name: BuiltInComponents.FormStep,
  isCanvas: true,
  rules: {
    canDrop: () => false,
    canDrag: () => false,
    canDelete: (node: CraftNode, helpers: NodeHelpersType) => {
      if (isSuccessFormStep(node)) {
        return false;
      }
      return helpers(node.id).getParent().childNodes().length > 2;
    },
    canMoveIn: (incomingNodes: CraftNode[]) =>
      incomingNodes.every(
        (node) =>
          ![BuiltInComponents.Form, BuiltInComponents.FormStep].includes(
            node.data.name as BuiltInComponents,
          ),
      ),
  },
};

FormStep.toolbox = {
  schema: {
    [ToolboxComponentSections.Size]: {
      hasMinMaxSize: false,
    },
  },
};

FormStep.flow = {
  shouldIgnoreFlowStep: (node: CraftNode) => isSuccessFormStep(node),
};
